import { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import { SvgMiscPlay } from "@ugitgud/legos/ui/icon/SVG/MiscPlay";
import { SvgMiscPause } from "@ugitgud/legos/ui/icon/SVG/MiscPause";

interface FeatureItem {
  title: string;
  description: string;
  image?: string;
  images?: string[];
  video?: string;
}

const FEATURES: FeatureItem[] = [
  {
    title: "Discord Integration",
    description:
      "Celebrate wins, roast losses, and compare stats with your Discord crew. Get automatic post-match summaries and a server leaderboard for up to 12 players.",
    images: [
      "https://static.bigbrain.gg/assets/ugg/icons/feature-discord-integration-1.webp",
      "https://static.bigbrain.gg/assets/ugg/icons/feature-discord-integration-2.webp",
    ],
  },
  {
    title: "Personal Counters & Stats",
    description:
      "Get insights built just for you. Curated stats on champion build pages, plus counters based on your past matchup history and performance.",
    image: "https://static.bigbrain.gg/assets/ugg/icons/feature-personal-counters-stats.webp",
  },
  {
    title: "Personal Tier List",
    description:
      "Showcase your mastery with a dedicated tier list page on your profile, highlighting your best-performing champions.",
    image: "https://static.bigbrain.gg/assets/ugg/icons/feature-personal-tier-list.webp",
  },
  {
    title: "Ad-free Experience",
    description: "Enjoy a stunning ad-free experience across our entire website and app.",
    image: "https://static.bigbrain.gg/assets/ugg/icons/feature-ad-free-experience.webp",
  },
  {
    title: "Profiles PLUS",
    description: "Your LoL profile will stand out amongst the rest with a golden flair and added custom features.",
    image: "https://static.bigbrain.gg/assets/ugg/icons/feature-profiles-plus.webp",
  },
  {
    title: "Shared Socials",
    description: "Gain followers on your favorite socials like Twitch, Discord, and X with links right on your profile.",
    image: "https://static.bigbrain.gg/assets/ugg/icons/feature-shared-socials.webp",
  },
  {
    title: "Priority Support",
    description: "Skip the queue with priority support, exclusively for PLUS members.",
    image: "https://static.bigbrain.gg/assets/ugg/icons/feature-priority-support.webp",
  },
];

const AUTO_SCROLL_INTERVAL = 5000;
const ACTIVE_FEATURE =
  "radial-gradient(50% 50% at 50% 100%, rgba(244, 200, 116, 0.31) 0%, rgba(244, 200, 116, 0.16) 14.05%, rgba(244, 200, 116, 0) 100%), #313160";

const FeatureMedia = ({
  feature,
  className,
  imageIndex,
  onVideoPlay,
  onVideoPaused,
  onVideoEnded,
}: {
  feature: FeatureItem;
  className: string;
  imageIndex?: number;
  onVideoPlay?: () => void;
  onVideoPaused?: () => void;
  onVideoEnded?: () => void;
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPause, setShowPause] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hideRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lingerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lingerElapsedRef = useRef<number>(0);
  const lingerStartedAtRef = useRef<number | null>(null);

  const startLinger = (remaining: number) => {
    lingerStartedAtRef.current = Date.now();
    lingerRef.current = setTimeout(() => {
      lingerRef.current = null;
      lingerElapsedRef.current = 0;
      lingerStartedAtRef.current = null;
      setIsPlaying(false);
      onVideoEnded?.();
    }, remaining);
  };

  const showPauseTemporarily = () => {
    setShowPause(true);
    if (hideRef.current) clearTimeout(hideRef.current);
    hideRef.current = setTimeout(() => setShowPause(false), 2000);
  };

  const handleMouseEnter = () => {
    if (isPlaying) showPauseTemporarily();
  };

  const handleMouseLeave = () => {
    if (hideRef.current) clearTimeout(hideRef.current);
    setShowPause(false);
  };

  if (feature.video) {
    return (
      <div className="relative w-full h-full" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <video
          ref={videoRef}
          src={feature.video}
          className={className}
          muted
          playsInline
          onEnded={() => {
            lingerElapsedRef.current = 0;
            startLinger(1000);
          }}
        />
        {isPlaying ? (
          <button
            type="button"
            aria-label="Pause video"
            className={classNames(
              "absolute inset-0 flex items-center justify-center transition-opacity",
              showPause ? "opacity-100" : "opacity-0",
            )}
            onClick={() => {
              if (lingerRef.current) {
                clearTimeout(lingerRef.current);
                lingerRef.current = null;
                lingerElapsedRef.current += Date.now() - (lingerStartedAtRef.current ?? Date.now());
                lingerStartedAtRef.current = null;
              } else {
                videoRef.current?.pause();
              }
              setIsPlaying(false);
              setShowPause(false);
              onVideoPaused?.();
            }}
          >
            <div className="flex items-center justify-center w-[56px] h-[56px] rounded-full bg-black/50 backdrop-blur-sm">
              <SvgMiscPause className="w-[30px] h-[30px] text-white" />
            </div>
          </button>
        ) : (
          <button
            type="button"
            aria-label="Play video"
            className="absolute inset-0 flex items-center justify-center"
            onClick={() => {
              if (lingerElapsedRef.current > 0 && !lingerRef.current) {
                startLinger(1000 - lingerElapsedRef.current);
              } else {
                videoRef.current?.play();
              }
              setIsPlaying(true);
              onVideoPlay?.();
              showPauseTemporarily();
            }}
          >
            <div className="flex items-center justify-center w-[56px] h-[56px] rounded-full bg-black/50 backdrop-blur-sm">
              <SvgMiscPlay className="w-[30px] h-[30px] text-white" />
            </div>
          </button>
        )}
      </div>
    );
  }

  if (feature.images) {
    return <img src={feature.images[imageIndex ?? 0]} alt={feature.title} className={className} />;
  }

  return <img src={feature.image} alt={feature.title} className={className} />;
};

const PaginationDots = ({
  images,
  activeIndex,
  onDotClick,
}: {
  images: string[];
  activeIndex: number;
  onDotClick: (i: number) => void;
}) => (
  <div className="absolute bottom-[4.4%] left-0 right-0 flex items-center justify-center gap-3">
    {images.map((_, i) => (
      <button
        key={i}
        type="button"
        aria-label={`Show image ${i + 1}`}
        aria-current={i === activeIndex ? "true" : undefined}
        onClick={() => onDotClick(i)}
        className={classNames(
          "w-3 h-3 rounded-[12px]",
          i === activeIndex ? "bg-accent-yellow-400" : "bg-accent-gray-200 transition-colors hover:bg-[#4A4A72]",
        )}
        style={
          i === activeIndex
            ? { boxShadow: "0 0 6.7px 0 rgba(244, 200, 116, 0.75), 0 0 14.9px 0 rgba(244, 200, 116, 0.50)" }
            : undefined
        }
      />
    ))}
  </div>
);

export const PlusFeaturesList = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [timerKey, setTimerKey] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const activeFeature = FEATURES[activeIndex];

  const goTo = (index: number) => {
    setActiveIndex(index);
    setActiveImageIndex(0);
    setVideoPlaying(false);
  };

  const goToImage = (index: number) => {
    if (index === activeImageIndex) return;
    setActiveImageIndex(index);
    setTimerKey((k) => k + 1);
  };

  useEffect(() => {
    const feature = FEATURES[activeIndex];
    if (feature.video || videoPlaying) return;

    if (feature.images && activeImageIndex < feature.images.length - 1) {
      const timer = setTimeout(() => setActiveImageIndex((i) => i + 1), AUTO_SCROLL_INTERVAL);
      return () => {
        clearTimeout(timer);
        setVideoPlaying(false);
      };
    }

    const timer = setTimeout(() => {
      setActiveIndex((i) => (i + 1) % FEATURES.length);
      setActiveImageIndex(0);
    }, AUTO_SCROLL_INTERVAL);
    return () => clearTimeout(timer);
  }, [activeIndex, activeImageIndex, timerKey, videoPlaying]);

  return (
    <div className="w-full max-w-[1013px] mx-auto mb-[40px] md:mb-[120px]">
      {/* Mobile */}
      <div className="flex flex-col md:hidden rounded-[3px] overflow-hidden w-full max-w-[677px] mx-auto bg-purple-200">
        {FEATURES.map((feature, index) => {
          const isActive = index === activeIndex;
          return (
            <div key={index}>
              {index > 0 && <div className="h-[1px] bg-purple-500" />}
              <button
                type="button"
                onClick={() => goTo(index)}
                className={classNames(
                  "flex w-full text-left text-[14px] transition-colors duration-200 h-[48px] pt-[14px] pl-[18px]",
                  isActive ? "font-semibold text-white border-b border-accent-yellow-400" : "text-lavender-50 bg-purple-400",
                )}
                style={isActive ? { background: ACTIVE_FEATURE } : undefined}
              >
                {feature.title}
              </button>
              {isActive && (
                <>
                  <div className="relative overflow-hidden w-full aspect-[677/408]">
                    <FeatureMedia
                      feature={feature}
                      className="w-full h-full object-cover"
                      imageIndex={activeImageIndex}
                      onVideoPlay={() => setVideoPlaying(true)}
                      onVideoPaused={() => setVideoPlaying(false)}
                      onVideoEnded={() => {
                        setActiveIndex((i) => (i + 1) % FEATURES.length);
                        setActiveImageIndex(0);
                        setVideoPlaying(false);
                      }}
                    />
                    {feature.images && feature.images.length > 1 && (
                      <PaginationDots images={feature.images} activeIndex={activeImageIndex} onDotClick={goToImage} />
                    )}
                  </div>
                  <div className="p-[18px] bg-lavender-500 border-t border-purple-500">
                    <p className="text-[12px] text-lavender-50 leading-snug">{feature.description}</p>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Desktop */}
      <div className="hidden md:flex rounded-[5px] overflow-hidden border border-[#2C2B4C] h-[507px]">
        {/* Left column */}
        <div className="flex flex-col w-[336px] shrink-0 h-full p-[18px] bg-purple-200">
          {FEATURES.map((feature, index) => {
            const isActive = index === activeIndex;
            const isFirst = index === 0;
            const isLast = index === FEATURES.length - 1;
            return (
              <button
                key={index}
                type="button"
                onClick={() => goTo(index)}
                className={classNames(
                  "flex w-[301px] h-[48px] mx-auto pt-[14px] pl-[18px]",
                  "text-[14px] transition-colors duration-200",
                  isFirst && "rounded-t-[3px]",
                  isLast && "rounded-b-[3px]",
                  isActive ? "border-b border-accent-yellow-400" : !isLast && "border-b border-purple-500",
                  isActive ? "text-white font-semibold" : "text-lavender-50 hover:text-white bg-purple-400",
                )}
                style={isActive ? { background: ACTIVE_FEATURE } : undefined}
              >
                {feature.title}
              </button>
            );
          })}
        </div>

        {/* Right column */}
        <div className="flex flex-col w-[677px] border-l border-purple-500">
          {/* Image/video section */}
          <div className="relative overflow-hidden flex-shrink-0 border-b border-purple-500 h-[408px]">
            <FeatureMedia
              key={activeIndex}
              feature={activeFeature}
              className="w-full h-full object-cover"
              imageIndex={activeImageIndex}
              onVideoPlay={() => setVideoPlaying(true)}
              onVideoPaused={() => setVideoPlaying(false)}
              onVideoEnded={() => {
                setActiveIndex((i) => (i + 1) % FEATURES.length);
                setActiveImageIndex(0);
                setVideoPlaying(false);
              }}
            />
            {activeFeature.images && activeFeature.images.length > 1 && (
              <PaginationDots images={activeFeature.images} activeIndex={activeImageIndex} onDotClick={goToImage} />
            )}
          </div>

          {/* Info box */}
          <div className="flex flex-col p-[18px] flex-1 bg-purple-400">
            <div className="border-l-[3px] border-accent-yellow-400 pl-[8px] mb-[12px]">
              <h3 className="text-[14px] font-bold text-white">{activeFeature.title}</h3>
            </div>
            <p className="text-[12px] text-lavender-50 leading-snug">{activeFeature.description}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
